import React, { useEffect, useState, useCallback } from 'react';
import { FormData } from '../types';
import { PlaceAutocomplete, PlaceAutocompleteResult } from '../../../shared/PlaceAutocomplete';
import {
  Input,
  Icon,
  BinaryToggle,
  DateInput,
  TimeInput,
  ContentBlock,
  MultiSelectToggle,
  RadioButtonGroup,
  NumberInput,
  Skeleton
} from '../../../../pages/DesignSystem';
import '../../../../styles/form-components.css';
import { COMMON_TIMEZONES, getTimezoneFromCoords } from '../../../../utils/timezoneUtils';
import { useGeolocation } from '../../../../hooks/useGeolocation';
import { useReverseGeocode } from '../../../../hooks/useReverseGeocode';
import { LocationPickerModal, LocationResult } from '../../../shared/LocationPicker';

interface BasicInfoStepProps {
  formData: FormData;
  setFormData: React.Dispatch<React.SetStateAction<FormData>>;
  errors: Partial<Record<keyof FormData, string>>;
  onPlaceSelect: (result: PlaceAutocompleteResult) => void;
  isLoaded: boolean;
  showLocationNameInput: boolean;
  isDraft: boolean;
  setShowLocationNameInput: React.Dispatch<React.SetStateAction<boolean>>;
  isLoadingTimezone?: boolean; // 🌍 État de chargement timezone
}

const WEEK_DAYS = [
  { id: 'monday', label: 'Mon' },
  { id: 'tuesday', label: 'Tue' },
  { id: 'wednesday', label: 'Wed' },
  { id: 'thursday', label: 'Thu' },
  { id: 'friday', label: 'Fri' },
  { id: 'saturday', label: 'Sat' },
  { id: 'sunday', label: 'Sun' }
];
const ALL_DAY_IDS = WEEK_DAYS.map(d => d.id);

const BasicInfoStep: React.FC<BasicInfoStepProps> = ({
  formData,
  setFormData,
  errors,
  onPlaceSelect,
  isLoaded,
  isLoadingTimezone = false
}) => {
  // Location picker state
  const [showMapPicker, setShowMapPicker] = useState(false);

  // GPS and reverse geocoding hooks
  const { getPosition, loading: geoLoading, error: geoError, clearError } = useGeolocation();
  const { reverseGeocode, loading: geocodeLoading } = useReverseGeocode();

  // Handle GPS button click
  const handleGPSClick = useCallback(async () => {
    const position = await getPosition();
    if (position) {
      // Reverse geocode to get address
      const result = await reverseGeocode(position.lat, position.lng);
      if (result) {
        setFormData(prev => ({
          ...prev,
          location: result.formattedAddress,
          locationName: result.locationName,
          latitude: result.latitude,
          longitude: result.longitude,
          geohash: result.geohash,
          isLocationNameManuallySet: false,
        }));
      }
    }
  }, [getPosition, reverseGeocode, setFormData]);

  // Handle map picker location confirmation
  const handleMapLocationConfirm = useCallback((locationResult: LocationResult) => {
    // 🌍 Detect timezone from coordinates
    const detectedTimezone = getTimezoneFromCoords(locationResult.latitude, locationResult.longitude);
    console.log(`🌍 [MapPicker] Timezone détecté: ${detectedTimezone}`);

    setFormData(prev => ({
      ...prev,
      location: locationResult.location,
      locationName: locationResult.locationName,
      latitude: locationResult.latitude,
      longitude: locationResult.longitude,
      geohash: locationResult.geohash,
      timezone: detectedTimezone,
      isLocationNameManuallySet: false,
    }));
  }, [setFormData]);

  // Clear GPS error after some time
  useEffect(() => {
    if (geoError) {
      const timer = setTimeout(() => clearError(), 5000);
      return () => clearTimeout(timer);
    }
  }, [geoError, clearError]);

  // Ajoutez ce code de débogage juste après la déclaration des useRef
  useEffect(() => {
    // console.log("🔍 BasicInfoStep - Statut de chargement Google Maps:", isLoaded);
    // console.log("📋 Vérification alternative:", isGoogleMapsLoaded());
    
    if (isLoaded) {
      // console.log("✅ BasicInfoStep - API Google Maps chargée");
      // console.log("🌐 window.google existe:", !!window.google);
      // console.log("🗺️ google.maps existe:", !!(window.google && window.google.maps));
      // console.log("📍 places API existe:", !!(window.google && window.google.maps && window.google.maps.places));
      // console.log("🔑 Autocomplete existe:", !!(window.google && window.google.maps && window.google.maps.places && window.google.maps.places.Autocomplete));
    } else {
      // console.log("❌ BasicInfoStep - API Google Maps non chargée");
    }
  }, [isLoaded]);

  const handleRecurrenceChange = (checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      isRecurring: checked,
      // When switching to recurring: copy date/time to startDate/startTime
      ...(checked === true && {
        startDate: prev.date || prev.startDate || '',
        startTime: prev.time || prev.startTime || '',
      }),
      // When switching to simple: copy startDate/startTime to date/time and reset recurring fields
      ...(checked === false && {
        date: prev.startDate || prev.date || '',
        time: prev.startTime || prev.time || '',
        startDate: '',
        endDate: '',
        endType: 'date',
        frequency: 'daily',
        weekDays: []
      })
    }));
  };

  const handleFrequencyChange = (value: boolean) => {
    setFormData(prev => {
      const newFrequency = value ? 'weekly' : 'daily';

      // Si on passe de weekly à daily avec endType 'occurrences',
      // convertir en 'date' pour préserver la plage de dates
      let newEndType = prev.endType;
      let newEndDate = prev.endDate;

      if (newFrequency === 'daily' && prev.frequency === 'weekly' && prev.endType === 'occurrences' && prev.occurrences && prev.startDate) {
        // Calculer la date de fin basée sur le nombre de semaines
        // Ex: 2 semaines = startDate + 14 jours
        const start = new Date(prev.startDate);
        const end = new Date(start);
        end.setDate(end.getDate() + (prev.occurrences * 7) - 1); // -1 car startDate est inclus
        newEndType = 'date';
        newEndDate = end.toISOString().split('T')[0];
      }

      // When switching to daily, clear weekDays AND daySchedules
      // Restore startTime from first daySchedule if needed
      const clearDaySchedules = newFrequency === 'daily' && prev.daySchedules;
      const firstDayTime = clearDaySchedules
        ? Object.values(prev.daySchedules!).find(s => s?.start)
        : null;

      return {
        ...prev,
        frequency: newFrequency,
        endType: newEndType,
        endDate: newEndDate,
        ...(newFrequency === 'daily' && {
          weekDays: [],
          daySchedules: null,
          // Restore startTime from daySchedules if it was empty
          ...(firstDayTime && !prev.startTime && {
            startTime: firstDayTime.start,
            endTime: firstDayTime.end || prev.endTime,
          }),
        })
      };
    });
  };

  const handleInputChange = (name: string) => (value: string | React.ChangeEvent<HTMLInputElement>) => {
    const newValue = typeof value === 'string' ? value : value.target.value;
    setFormData(prev => {
      const newData = { ...prev, [name]: newValue };
      return newData;
    });
  };

  const handleLocationNameChange = (value: string | React.ChangeEvent<HTMLInputElement>) => {
    const newValue = typeof value === 'string' ? value : value.target.value;
    setFormData(prev => {
      const newData = { 
        ...prev, 
        locationName: newValue,
        isLocationNameManuallySet: true
      };
      return newData;
    });
  };

  const handleEndTypeChange = (value: string) => {
    if (value !== 'date' && value !== 'occurrences') return;
    
    setFormData(prev => ({
      ...prev,
      endType: value,
      // Réinitialiser les valeurs non pertinentes
      ...(value === 'date' ? { occurrences: undefined } : { endDate: '' })
    }));
  };

  // Calculer la date maximale (1 mois après la date de début)
  const getMaxEndDate = () => {
    if (!formData.startDate) return '';
    const startDate = new Date(formData.startDate);
    const maxDate = new Date(startDate);
    maxDate.setMonth(maxDate.getMonth() + 1);
    return maxDate.toISOString().split('T')[0];
  };

  const handleOccurrencesChange = (newValue: number) => {
    // Limiter à 4 semaines maximum
    const limitedValue = Math.min(newValue, 4);
    setFormData(prev => ({
      ...prev,
      occurrences: limitedValue
    }));
  };


  return (
    <div className="event-form__step-content">
      {/* Title Section */}
      <ContentBlock
        title="Event Title"
        border="light"
      >
        <div className="event-form__group">
          <label className="event-form__label event-form__label--required">
            Title
            <span className="event-form__label-hint">
              (Give your event a clear name that people will understand)
            </span>
          </label>
          <Input
            id="title"
            name="title"
            type="text"
            value={formData.title || ''}
            onChange={handleInputChange('title')}
            error={errors.title}
            placeholder="Example: Weekly Yoga Class, Monthly Book Club..."
          />
        </div>
      </ContentBlock>

      {/* Location Section */}
      <ContentBlock
        title="Location Details"
        spacing="md"
        border="light"
      >
        <div className="event-form__group">
          <label className="event-form__label">
            Adress
            <span className="event-form__label-hint">
              (Where will your event take place?)
            </span>
          </label>
          {isLoaded ? (
            <div className="event-form__location">
              <div className="event-form__location-input-row">
                <div className="event-form__location-autocomplete">
                  <PlaceAutocomplete
                    placeholder="Search for a place or enter an address..."
                    value={formData.location || ''}
                    onChange={(value) => setFormData(prev => {
                      // If the text no longer matches what's stored, the organizer is typing
                      // freely (not selecting a suggestion). Invalidate any previously-set
                      // coordinates so they can't sneak into publication without a fresh pick.
                      // A real selection immediately re-sets valid coords via onPlaceSelect
                      // right after this fires, so this never blocks the normal select flow.
                      if (value === (prev.location || '')) return prev;
                      return {
                        ...prev,
                        location: value,
                        latitude: undefined,
                        longitude: undefined,
                        geohash: undefined,
                      };
                    })}
                    onPlaceSelect={onPlaceSelect}
                    error={errors.location}
                    className="event-form__input"
                  />
                </div>

                {/* GPS and Map Picker buttons */}
                <div className="event-form__location-actions">
                  <button
                    type="button"
                    className={`event-form__location-btn ${geoLoading ? 'event-form__location-btn--loading' : ''}`}
                    onClick={handleGPSClick}
                    disabled={geoLoading || geocodeLoading}
                    title="Use my location"
                  >
                    <Icon name={geoLoading ? 'Loader' : 'Locate'} size={18} />
                  </button>
                  <button
                    type="button"
                    className="event-form__location-btn"
                    onClick={() => setShowMapPicker(true)}
                    title="Pick on map"
                  >
                    <Icon name="Map" size={18} />
                  </button>
                </div>
              </div>

              {/* GPS Error message */}
              {geoError && (
                <div className="event-form__location-error">
                  <Icon name="AlertCircle" size={14} />
                  <span>{geoError}</span>
                </div>
              )}

              <div className="event-form__group">
                <label className="event-form__label">
                  Place Name
                  <span className="event-form__label-hint">
                    (Name of the place people will be looking for)
                  </span>
                  <span className="event-form__label-hint">
                    {formData.locationName && !formData.isLocationNameManuallySet && (
                      " (auto-filled)"
                    )}
                  </span>
                </label>
                <Input
                  id="locationName"
                  name="locationName"
                  type="text"
                  value={formData.locationName || ''}
                  onChange={handleLocationNameChange}
                  placeholder="Enter a name people will recognize..."
                  error={errors.locationName}
                  icon={formData.locationName && !formData.isLocationNameManuallySet ? "Building2" : undefined}
                />
                {formData.locationName && !formData.isLocationNameManuallySet && (
                  <span className="event-form__label-hint">
                    You can change this name if needed
                  </span>
                )}
              </div>

              {/* 🌍 Timezone indicator - loading state */}
              {isLoadingTimezone && (
                <div className="event-form__group event-form__group--timezone">
                  <label className="event-form__label">
                    <Icon name="Globe" size={14} />
                    {' '}Timezone
                    <span className="event-form__label-hint">
                      (detecting...)
                    </span>
                  </label>
                  <Skeleton variant="rectangle" height="40px" width="100%" />
                </div>
              )}

              {/* 🌍 Timezone selector - always visible */}
              {!isLoadingTimezone && (
                <div className="event-form__group event-form__group--timezone">
                  <label className="event-form__label">
                    <Icon name="Globe" size={14} />
                    {' '}Timezone
                    <span className="event-form__label-hint">
                      {formData.latitude && formData.longitude && formData.timezone
                        ? "(auto-detected from location)"
                        : "(select location to auto-detect)"}
                    </span>
                  </label>
                  <select
                    className="event-form__timezone-select"
                    value={formData.timezone || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, timezone: e.target.value }))}
                  >
                    {/* Placeholder option when no timezone */}
                    {!formData.timezone && (
                      <option value="" disabled>
                        Select a timezone...
                      </option>
                    )}
                    {/* Option for auto-detected timezone if not in common list */}
                    {formData.timezone && !COMMON_TIMEZONES.some(tz => tz.value === formData.timezone) && (
                      <option value={formData.timezone}>
                        {formData.timezone} (detected)
                      </option>
                    )}
                    {COMMON_TIMEZONES.map(tz => (
                      <option key={tz.value} value={tz.value}>
                        {tz.label}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          ) : (
            <Input
              id="location-loading"
              name="location-loading"
              type="text"
              value=""
              onChange={() => {}}
              disabled
              placeholder="Loading..."
              icon="Loader"
            />
          )}
        </div>
      </ContentBlock>

      {/* Date and Time Section */}
      <ContentBlock
        title="Event Schedule"
        border="light"
      >
        <div className="event-form__group">
          <div className="event-form__recurrence-question">
            <span className="event-form__label">How often does this event happen?</span>
            {formData.isEditingOccurrence && (
              <span className="event-form__label-hint" style={{ color: '#8B5CF6', fontStyle: 'italic' }}>
                ✏️ You're editing a single occurrence from a recurring event series
              </span>
            )}
          </div>
          <BinaryToggle
            leftLabel="Just once"
            rightLabel="Repeats"
            value={formData.isRecurring}
            onChange={handleRecurrenceChange}
            disabled={formData.isEditingOccurrence} // ✨ Désactiver si on édite une occurrence
          />
          {formData.isEditingOccurrence && (
            <div className="event-form__help-text" style={{ 
              color: '#6366F1', 
              fontSize: '13px', 
              marginTop: '8px',
              padding: '8px 12px',
              backgroundColor: '#F3F4F6',
              borderRadius: '6px',
              border: '1px solid #E5E7EB'
            }}>
              💡 <strong>Editing individual occurrence:</strong> Changes will only apply to this specific date and won't affect other events in the series.
            </div>
          )}
        </div>

        {formData.isRecurring ? (
          <div className="event-form__recurrence">
            <div className="event-form__group event-form__group--row">
              <div className="event-form__date">
                <label className="event-form__label">Start Date</label>
                <DateInput
                  id="start-date"
                  name="start-date"
                  value={formData.startDate || ''}
                  onChange={handleInputChange('startDate')}
                  error={errors.startDate}
                />
              </div>
              {/* Show uniform time inputs only when NOT using per-day schedules */}
              {!formData.daySchedules && (
                <div className="event-form__time-range">
                  <label className="event-form__label">Event Time</label>
                  <div className="event-form__time-inputs">
                    <div className="event-form__time-from">
                      <span className="event-form__time-label">From</span>
                      <TimeInput
                        id="start-time"
                        name="start-time"
                        value={formData.startTime || ''}
                        onChange={handleInputChange('startTime')}
                        error={errors.startTime}
                      />
                    </div>
                    <div className="event-form__time-to">
                      <span className="event-form__time-label">to</span>
                      <TimeInput
                        id="end-time"
                        name="end-time"
                        value={formData.endTime || ''}
                        onChange={handleInputChange('endTime')}
                        error={errors.endTime}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="event-form__group">
              <div className="event-form__frequency-question">
                <span className="event-form__label">How often does this event repeat?</span>
              </div>
              <BinaryToggle
                leftLabel="Some days"
                rightLabel="Every day"
                value={formData.frequency !== 'weekly'}
                onChange={(value) => handleFrequencyChange(!value)}
                className="frequency-toggle"
              />
            </div>

            {formData.frequency === 'weekly' && (
              <div className="event-form__group">
                <label className="event-form__label">
                  Which days of the week?
                  <span className="event-form__label-hint">
                    (Select all days when the event will happen)
                  </span>
                </label>
                <MultiSelectToggle
                  options={WEEK_DAYS}
                  selectedValues={formData.weekDays || []}
                  onChange={(selectedDays) => {
                    setFormData(prev => {
                      let newDaySchedules = prev.daySchedules;

                      if (selectedDays.length < 2) {
                        // Less than 2 days: revert to uniform schedule
                        const firstDayTime = newDaySchedules
                          ? Object.values(newDaySchedules).find(s => s?.start)
                          : null;
                        return {
                          ...prev,
                          weekDays: selectedDays,
                          daySchedules: null,
                          // Restore startTime from daySchedules if needed
                          ...(firstDayTime && !prev.startTime && {
                            startTime: firstDayTime.start,
                            endTime: firstDayTime.end || prev.endTime,
                          }),
                        };
                      }

                      // If using per-day schedules, remove schedules for deselected days
                      // and add empty schedule for newly selected days
                      if (newDaySchedules) {
                        newDaySchedules = { ...newDaySchedules };
                        Object.keys(newDaySchedules).forEach(day => {
                          if (!selectedDays.includes(day)) {
                            delete newDaySchedules![day];
                          }
                        });
                        // Add empty schedule for newly selected days
                        selectedDays.forEach(day => {
                          if (!newDaySchedules![day]) {
                            newDaySchedules![day] = { start: '', end: '' };
                          }
                        });
                      }
                      return { ...prev, weekDays: selectedDays, daySchedules: newDaySchedules };
                    });
                  }}
                />
                {errors.weekDays && (
                  <span className="event-form__error">{errors.weekDays}</span>
                )}
              </div>
            )}

            {/* Variable schedule toggle: show for daily OR weekly with 2+ days */}
            {(formData.frequency === 'daily' || (formData.frequency === 'weekly' && (formData.weekDays?.length || 0) >= 2)) && (
              <div className="event-form__group">
                <span className="event-form__label">Same time every day?</span>
                <BinaryToggle
                  leftLabel="Yes, same time"
                  rightLabel="No, it varies"
                  value={!!formData.daySchedules}
                  onChange={(useVariableSchedule) => {
                    if (useVariableSchedule) {
                      // For daily: use all 7 days; for weekly: use selected days
                      const days = formData.frequency === 'daily' ? ALL_DAY_IDS : (formData.weekDays || []);
                      const initial: Record<string, { start: string; end: string }> = {};
                      days.forEach(day => {
                        initial[day] = {
                          start: formData.startTime || '',
                          end: formData.endTime || '',
                        };
                      });
                      setFormData(prev => ({
                        ...prev,
                        daySchedules: initial,
                        // For daily, populate weekDays so per-day inputs render
                        ...(formData.frequency === 'daily' && { weekDays: ALL_DAY_IDS }),
                      }));
                    } else {
                      // Revert to uniform: use the first day's schedule as the base
                      const firstDayKey = formData.frequency === 'daily' ? ALL_DAY_IDS[0] : (formData.weekDays?.[0] || '');
                      const firstDaySchedule = formData.daySchedules?.[firstDayKey];
                      setFormData(prev => ({
                        ...prev,
                        daySchedules: null,
                        startTime: firstDaySchedule?.start || prev.startTime || '',
                        endTime: firstDaySchedule?.end || prev.endTime || '',
                        // For daily, clear weekDays back (they're implicit)
                        ...(formData.frequency === 'daily' && { weekDays: [] }),
                      }));
                    }
                  }}
                />
              </div>
            )}

            {/* Per-day time inputs when variable schedule is active */}
            {formData.daySchedules && formData.weekDays && formData.weekDays.length > 0 && (
              <div className="event-form__group">
                <label className="event-form__label">Time per day</label>
                <div className="event-form__day-schedules">
                  {WEEK_DAYS
                    .filter(day => formData.weekDays?.includes(day.id))
                    .map(day => (
                      <div key={day.id} className="event-form__day-schedule-row">
                        <span className="event-form__day-schedule-label">{day.label}</span>
                        <div className="event-form__time-inputs event-form__time-inputs--compact">
                          <TimeInput
                            id={`day-start-${day.id}`}
                            name={`day-start-${day.id}`}
                            value={formData.daySchedules?.[day.id]?.start || ''}
                            onChange={(value: string | React.ChangeEvent<HTMLInputElement>) => {
                              const val = typeof value === 'string' ? value : value.target.value;
                              setFormData(prev => ({
                                ...prev,
                                daySchedules: {
                                  ...prev.daySchedules,
                                  [day.id]: {
                                    start: val,
                                    end: prev.daySchedules?.[day.id]?.end || '',
                                  },
                                },
                              }));
                            }}
                          />
                          <span className="event-form__time-label">to</span>
                          <TimeInput
                            id={`day-end-${day.id}`}
                            name={`day-end-${day.id}`}
                            value={formData.daySchedules?.[day.id]?.end || ''}
                            onChange={(value: string | React.ChangeEvent<HTMLInputElement>) => {
                              const val = typeof value === 'string' ? value : value.target.value;
                              setFormData(prev => ({
                                ...prev,
                                daySchedules: {
                                  ...prev.daySchedules,
                                  [day.id]: {
                                    start: prev.daySchedules?.[day.id]?.start || '',
                                    end: val,
                                  },
                                },
                              }));
                            }}
                          />
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            )}

            <div className="event-form__group">
              <label className="event-form__label">When does it end?</label>
              <RadioButtonGroup
                name="end-type"
                options={[
                  {
                    value: 'date',
                    label: 'Until a specific date',
                    contentInline: true,
                    description: 'Up to 1 month after the start date',
                    content: formData.endType === 'date' && (
                      <DateInput
                        id="end-date"
                        name="end-date"
                        value={formData.endDate || ''}
                        onChange={handleInputChange('endDate')}
                        error={errors.endDate}
                        min={formData.startDate || ''}
                        max={getMaxEndDate()}
                      />
                    )
                  },
                  {
                    value: 'occurrences',
                    label: 'After a number of weeks',
                    contentInline: true,
                    description: 'Choose between 1 and 4 weeks',
                    content: formData.endType === 'occurrences' && (
                      <NumberInput
                        id="occurrences"
                        name="occurrences"
                        value={formData.occurrences || 1}
                        onChange={handleOccurrencesChange}
                        error={errors.occurrences}
                        min={1}
                        max={4}
                      />
                    )
                  }
                ]}
                onChange={handleEndTypeChange}
                defaultValue={formData.endType}
              />
            </div>
          </div>
        ) : (
          <div className="event-form__group event-form__group--row">
            <div className="event-form__date">
              <label className="event-form__label">Event Date</label>
              <DateInput
                id="event-date"
                name="event-date"
                value={formData.date || ''}
                onChange={handleInputChange('date')}
                error={errors.date}
              />
            </div>
            <div className="event-form__time-range">
              <label className="event-form__label">Event Time</label>
              <div className="event-form__time-inputs">
                <div className="event-form__time-from">
                  <span className="event-form__time-label">From</span>
                  <TimeInput
                    id="event-time"
                    name="event-time"
                    value={formData.time || ''}
                    onChange={handleInputChange('time')}
                    error={errors.time}
                  />
                </div>
                <div className="event-form__time-to">
                  <span className="event-form__time-label">to</span>
                  <TimeInput
                    id="event-end-time"
                    name="event-end-time"
                    value={formData.endTime || ''}
                    onChange={handleInputChange('endTime')}
                    error={errors.endTime}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </ContentBlock>

      {/* Location Picker Modal */}
      <LocationPickerModal
        isOpen={showMapPicker}
        onClose={() => setShowMapPicker(false)}
        onConfirm={handleMapLocationConfirm}
        initialPosition={
          formData.latitude && formData.longitude
            ? { lat: formData.latitude, lng: formData.longitude }
            : undefined
        }
      />
    </div>
  );
};

export default BasicInfoStep;
